import { As, ButtonProps, ChakraProps } from '@chakra-ui/react';
import { ElementType, ReactNode } from 'react';

export type MhyImage = null | {
  src: string | null;
  alt: string | null;
};

export interface Link {
  url: string;
  title: string;
  target?: '_self' | '_blank';
}

export type SiteLocale = 'fi' | 'sv';

export interface AcfButtonLink extends ButtonProps {
  link: {
    url?: string;
    onClick?: ButtonProps['onClick'];
    target?: '_blank' | '_self';
    title?: string;
    locale?: SiteLocale;
  };
}

export interface AcfHeading {
  title: string;
  style?: string;
  importance: string | ElementType; // should limit to HTMLHeadingElement but dont know how
}

export interface AcfServiceTab {
  title: string;
  text: string;
}

export interface AcfServiceTabs {
  name: string;
  title: string;
  content: string | AcfServiceTab[];
}

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormField {
  name: string;
  type:
    | string
    | 'number'
    | 'text'
    | 'textarea'
    | 'email'
    | 'select'
    | 'checkbox'
    | 'fieldset';
  label: ReactNode;
  required: boolean;
  info?: string;
  options?:
    | null
    | false
    | (FormFieldOption | { label: string; options: FormFieldOption[] })[];
  fields?:
    | null
    | false
    | (Omit<FormField, 'type'> & {
        type: Exclude<FormField['type'], 'fieldset'>;
      })[];
}

export interface SpecialFormField extends FormField {
  field_type: FormField['type'];
}

export type FormFieldTypes = FormField | SpecialFormField;

export type MhyFormType =
  | 'service_offer'
  | 'event'
  | 'contact'
  | 'membership'
  | 'feedback'
  | 'address';

export interface Form {
  fields: FormFieldTypes[];
  id: number;
  slots?: number | null;
  type?: MhyFormType;
  title?: string;
  lead?: string;
  service?: FormFieldOption;
  legal?: [] | Link[];
}

export interface Author {
  first_name: string;
  last_name: string;
  description: string;
}

export interface Blogger extends Author {
  roles: string | null;
  image: MhyImage | null;
}

export interface NewsTeaser {
  id: number;
  url: string;
  title: string;
  image: MhyImage;
  lead: string;
  site: string;
  date?: null | string;
  wysiwyg?: null | string;
  author?: Blogger | null;
  tags?: string[];
}

export interface NewsTeasers {
  title: string;
  entries: null | NewsTeaser[];
  all_link: null | Link;
}

export type BenefitTeaser = Omit<NewsTeaser, 'date' | 'url'>;

export interface SelectedEntriesField {
  heading: string;
  entries?: (NewsTeaser | null)[];
  link?: null | Link;
}

export interface AccordionBlockItemProps {
  title: string;
  text: string;
  image: string;
  label: string;
}

export interface AccordionBlockProps {
  data?: AccordionBlockItemProps[];
  accordionType: 'basic' | 'section';
}

export interface MhyCtaButton extends Omit<ButtonProps, 'form'> {
  title: string;
}

export interface MhyFormButton extends MhyCtaButton {
  form: Form;
}

export interface ServiceOrderRequestColumn {
  heading?: AcfHeading;
  text?: string;
  buttons: (AcfButtonLink | MhyCtaButton)[] | false;
}
export interface ServiceOrderRequestContact {
  title?: string;
  text?: string;
  phone?: string;
  email?: string;
}
export interface ServiceOrderRequestProps {
  title: string;
  content: ServiceOrderRequestColumn[];
  contact: ServiceOrderRequestContact;
}

export interface HeroProps {
  name?: string;
  heading: AcfHeading;
  text?: string;
  wysiwyg?: string;
  buttons?: false | AcfButtonLink[];
  image: MhyImage;
  campaign?: true;
}

export interface ServiceTabsProps {
  data: AcfServiceTabs[];
}

export interface ServiceTestimonialsProps {
  title?: string;
  testimonials: {
    id: number;
    name: string;
    content: string;
    date: string;
  }[];
}

export type WoodType =
  | 'Kuusitukki'
  | 'Kuusikuitu'
  | 'Mäntytukki'
  | 'Mäntykuitu'
  | 'Koivutukki'
  | 'Koivukuitu';

export const WoodCuttingTypes = [
  'Uudistushakkuu',
  'Ensiharvennus',
  'Harvennus',
  'Kaikki hakkuut',
] as const;

export type WoodCuttingType = (typeof WoodCuttingTypes)[number];

export type PriceOfWoodFromQuickBi = {
  puutavaralaji: WoodType;
  hakkuutapa: WoodCuttingType;
  viimeisin_hinta: number;
  edeltava_hinta: number;
  muutos: number;
  muutos_suhteellinen: number;
};
export interface PriceOfWood {
  prices: PriceOfWoodFromQuickBi[];
  title: string;
  subtitle: string;
  text: string;
  link: Link;
  filter_title: string;
  to_everyone: string;
  to_members: string;
  promotion: {
    heading: string;
    wysiwyg_all: string;
    wysiwyg_members: string;
    link: AcfButtonLink;
  };
}

export interface ServiceCategoryTeasersProps {
  title?: string;
  services?: {
    title: string;
    icon: string;
    text: string;
    url: string;
    color: string;
    link_text: string;
  }[];
}

export interface YoutubeVideEmbedProps {
  videoSrc: string;
  title?: string;
  heroMode?: boolean;
  isHidden?: boolean;
}

export interface VelloEmbedProps {
  id: string;
}
export interface IntegrationBlockProps {
  title?: string;
  type: 'bot_slot';
  bot_id?: string;
}

export interface KeyFiguresProps {
  title?: string;
  data: { value: string; name: string }[];
}

export interface ForestExpertProps {
  title: string | null;
  content: string | null;
  image: MhyImage;
}

export interface TeaserMembershipProps {
  heading: AcfHeading;
  buttons: AcfButtonLink[] | false;
  wysiwyg?: string;
  content_right:
    | {
        heading: AcfHeading;
        image: MhyImage;
        text: string;
      }[]
    | null;
}

export interface FeaturesBlockProps {
  features:
    | {
        heading: AcfHeading;
        image: MhyImage;
        text: string;
      }[]
    | null;
}

export interface ElectionsCtaProps {
  heading: AcfHeading;
  buttons: AcfButtonLink[] | false;
  text?: string;
  content_right: {
    heading: AcfHeading;
    text: string;
  } | null;
}
export interface ElectionResultsProps {
  id: string;
}

export interface Heading {
  title: string;
  importance: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  style: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
}

export interface AccordionElement {
  title?: string;
  content?: string;
}

export interface Newsletter {
  title: string;
  lead: string;
  link: Link;
}

export interface Location {
  address: string;
  lat: number;
  lng: number;
  zoom: number;
  place_id: string;
  name: string;
  street_number: string;
  street_name: string;
  city?: string;
  state?: string;
  post_code: string;
  country: string;
  country_short: string;
}

export type NormalizedLocation = {
  name?: string;
  street?: string;
  number?: string;
  cityOrState?: string;
  postalCode?: string;
  placeId?: string;
};

export type EventPrice = string | { membership: string; others: string };

export interface Event {
  id: number;
  location: false | Location;
  display_address: null | string;
  is_webinar?: boolean;
  start_time: string;
  end_time: string;
  registration_end_time: string;
  price: EventPrice;
  site: string;
  url: string;
  title: string;
}

export interface OfficeTeaser {
  title: string;
  location: null | Location;
  display_address: null | string;
  email: null | string;
  phone: null | string;
  description: null | string;
  all_link: null | Link;
}

export interface OfficeTeasers {
  offices: OfficeTeaser[];
}

export interface ForestAccountCalculator {
  title: string;
  loanAmount: {
    title: string;
    min: string;
    max: string;
    step: string;
  };
  loanTime: {
    title: string;
    min: string;
    max: string;
  };
  instructions: string;
  link: AcfButtonLink;
}

export interface EventTeasers {
  title: string;
  data: Event[];
  all_link: null | Link;
}
export interface ServiceFormCalendar {
  id: string;
}
export interface ContactBlock {
  title: string;
  form: Form | null;
}
export interface MembershipBlock {
  title: string;
  form: Form | null;
}

export interface Term {
  term_id: number;
  name: string;
  slug: string;
  term_group: number;
  term_taxonomy_id: number;
  taxonomy: string;
  description: string;
  parent: number;
  count: number;
  filter: string;
  term_order: string;
}

export type Tag = Pick<Term, 'slug' | 'name'>;

export type SimpleTerm = Pick<Term, 'slug' | 'name'> & {
  id: Term['term_id'];
};

export interface Person {
  firstname: string;
  surname: string;
  fullname?: string;
  phone?: string;
  email?: string;
  extra_text?: string;
  extra_link?: Link;
  showOfficeDetails?: (blog_id: number, office_id: number | null) => void;
  id: number;
  office: SimpleTerm[] | string[];
  territory: string[];
  roles?: string;
  image_src?: string;
  image_src_md?: string;
  blog_id: number;
  site?: string;
  municipality?: string;
}

export interface BoardMembers {
  title: string | null;
  members: Person[];
}

export interface FormTab {
  title: string;
  wysiwyg?: null | string;
  form?: null | Form;
}

export type FormTabs = FormTab[];

export interface MembershipFee {
  desc: null | string;
  content: null | string;
}

export interface PostContentAccordionItemProps {
  slug: string;
  title: string;
  image: MhyImage;
  content?: {
    __name?: string;
    html?: string;
  }[];
  video?: 'string';
}

export interface ElectionsAccordionProps {
  heading: AcfHeading;
  entries: PostContentAccordionItemProps[];
}

export interface PostContentAccordionProps {
  data: PostContentAccordionItemProps[];
}

export interface Field {
  __name?: string;
  html?: string;
  data?:
    | SelectedEntriesField
    | TeaserMembershipProps
    | AcfServiceTabs[]
    | PostContentAccordionItemProps[]
    | HeroProps
    | ElectionsAccordionProps
    | AccordionElement[]
    | KeyFiguresProps
    | ForestExpertProps
    | ServiceFormCalendar
    | EventTeasers
    | NewsTeasers
    | ServiceTestimonialsProps
    | ServiceCategoryTeasersProps
    | ServiceOrderRequestProps
    | MembershipFee
    | BoardMembers
    | OfficeTeasers;
}

export type Breadcrumb = Link;

// Meta is strings as long as it's not breadcrumb, which is Breadcrumb. Funny syntax just because of TypeScript limitations
export interface PostMeta {
  meta: {
    [key: string]: string;
  } & {
    breadcrumb: never;
  } & {
    breadcrumb: Breadcrumb[];
  };
  edit_link?: string; // when in admin mode
}

export interface TranslationType {
  [key: string]: string;
}

export type Translations = {
  [lang: string]: string;
};

export interface Post {
  ID: number;
  fields: Field[];
  post_date: string;
  post_excerpt: string;
  post_title: string;
  post_type:
    | 'job'
    | 'post'
    | 'page'
    | 'blog'
    | 'service-page'
    | 'shared-page'
    | 'shared-election-page'
    | 'event';
  template?: string;
  translations: Translations;
  url: string;
  featured_image: null | MhyImage;
  related?: null | { title: string; items: NewsTeaser[] };
  shareable?: null | true;
}

export interface ServicePost
  extends Pick<
    Post,
    'url' | 'ID' | 'post_title' | 'post_excerpt' | 'featured_image'
  > {
  categories: string[] | Term[];
}

export interface SelectedServiceField {
  services?: ServicePost[];
  categories: Term[];
}

export interface PeoplePagePost extends Post {
  data: Person[];
}

export interface EventsPagePost extends Post {
  data: Event[];
}

export interface ElectionPagePost extends Post {
  data?: {
    hero?: HeroProps;
  };
}
export interface MyForestPost extends Post {
  data?: {
    hero?: HeroProps;
  };
}
export interface CampaignPagePost extends Post {
  data?: {
    hero?: HeroProps;
  };
}

export interface AssociationPage extends Post {
  data: {
    office: null | OfficeTeaser;
    location: false | Location;
  };
}

export interface NewsPage extends Post {
  data: NewsTeaser[];
}

export interface JobsPage extends Post {
  data: NewsTeaser[];
}

export interface BlogsPage extends Post {
  data: null | {
    items: NewsTeaser[];
    tags: Tag[];
  };
}

export interface BenefitsPage extends Post {
  data: null | {
    benefits: BenefitTeaser[];
  };
}

export interface ServiceLandingPost extends Post {
  data?: {
    sub_services?: ServicePost[];
    service_categories?: Term[];
    hero?: HeroProps;
  };
}

export interface EventPost extends Post {
  post_type: 'event';
  data: Pick<
    Event,
    | 'id'
    | 'display_address'
    | 'location'
    | 'price'
    | 'end_time'
    | 'start_time'
    | 'registration_end_time'
    | 'is_webinar'
  > & {
    next_events: EventTeasers | null;
    info: string | null;
    form: null | Form;
    related: null | NewsTeaser;
  };
}

export interface Article extends Post {
  author: null | Blogger | Author;
}

export interface NavPost {
  ID?: number;
  children?: NavPost[] | null;
  title?: string;
  url?: string;
}

export interface AnnouncementBar {
  title: string | null;
  text: string;
  link: Link | null;
}

export interface SiteNav {
  main_navigation: NavPost[];
  legal_navigation: {
    title: string;
    url: string;
  }[];
  header_links: {
    membership: Link | null;
    login: Link | null;
  };
  social_media_links: {
    [key: string]: string;
  }[];
  announcement_bar: AnnouncementBar | null;
}

export interface ServiceData {
  subject_type: null | { value: string; label: string };
  timing: null | { value: string; label: string };
  estimate: null | { value: string; label: string };
}

export interface ServicePage extends Post {
  featured_video?: null | string;
  data: null | ServiceData;
}

export type SearchResult = Pick<Post, 'url' | 'ID' | 'post_title'>;

export interface Site {
  blog_id: number;
  domain: string;
  path: string;
  name: string;
  languages?: string[] | null;
  blogname: string;
  tags: string[];
}

interface InterpolateArgs {
  [key: string]: string | number;
}

export type Translate = (key: string, args?: InterpolateArgs) => string;

// Mucha better chakraprops
export interface MhyChakraProps extends ChakraProps {
  variant?: string;
  as?: As;
  className?: string;
}

export interface EntryPageAppProps {
  metadata?: {
    image: string | null;
    title: string | null;
    canonical: string;
    description: string | null;
    breadcrumb: Breadcrumb[] | null;
    url: string;
  } | null;
  navigations?: null | SiteNav;
  translationData?: null | TranslationType;
  translations?: null | Translations;
  data?: null | Post;
  subsite?: string | null;
  allSites?: Site[] | null;
  sites?: Site[] | null;
  consenting: boolean;
}

export interface Cookiebot {
  show(): void;
  renew(): void;
  CookiebotOnConsentReady;
  consented: boolean;
  hasResponse: boolean;
  changed: boolean;
  consent: {
    necessary: boolean;
    marketing: boolean;
    preferences: boolean;
    statistics: boolean;
  };
}

export interface Leadoo {
  Chat?: {
    first: any;
    last: any;
  };
}

declare global {
  interface Window {
    Cookiebot?: Cookiebot;
    Leadoo?: Leadoo;
  }
}
